import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Clock,
  Calendar,
  Filter,
  Download,
  ChevronDown,
  ChevronLeft,
  Play,
  PenLine,
  Trash2,
  BarChart3,
  TrendingUp,
  Users,
  FileText,
  Search,
  ArrowUpDown,
  AlertTriangle,
  CheckCircle,
  Timer,
} from "lucide-react";
import { timeEntryAPI, cardAPI } from "../../utils/api";
import { useNotification } from "../../contexts/NotificationContext";
import { useUser } from "../../contexts/UserContext";
import Avatar from "../Avatar";
import { formatDuration, formatWorkDate } from "./timeUtils";
import CardModal from "../CardModal";

const TimeReportsModal = ({ isOpen, onClose, projectId, projectName }) => {
  const { showToast } = useNotification();
  const { user } = useUser();

  // View mode state
  const [viewMode, setViewMode] = useState("entries"); // "cards" | "entries"

  // Data state
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ users: [], cards: [], labels: [] });
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  // Card modal state
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [loadingCard, setLoadingCard] = useState(false);

  // Handle card click to open modal
  const handleCardClick = async (card) => {
    if (!card?._id) return;

    try {
      setLoadingCard(true);
      const response = await cardAPI.getCard(card._id);
      if (response.data.success) {
        setSelectedCard(response.data.card);
        setShowCardModal(true);
      }
    } catch (error) {
      console.error("Error fetching card details:", error);
      showToast("Failed to load card details", "error");
    } finally {
      setLoadingCard(false);
    }
  };

  const handleCardModalClose = () => {
    setShowCardModal(false);
    setSelectedCard(null);
  };

  const handleCardUpdated = (updatedCard) => {
    fetchEntries(pagination.page);
    fetchSummary();
    if (selectedCard?._id === updatedCard._id) {
      setSelectedCard(updatedCard);
    }
  };

  const handleCardDeleted = () => {
    fetchEntries(1);
    fetchSummary();
    handleCardModalClose();
  };

  // Filter state
  const emptyFilters = {
    startDate: "",
    endDate: "",
    userIds: [],
    cardId: "",
    labelNames: [],
    entryType: "",
  };

  const [filters, setFilters] = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [showMemberFilter, setShowMemberFilter] = useState(false);
  const [showLabelFilter, setShowLabelFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Edit state
  const [editingEntry, setEditingEntry] = useState(null);
  const [editDuration, setEditDuration] = useState({ hours: "", minutes: "" });
  const [editDescription, setEditDescription] = useState("");

  // Sort state for cards view
  const [cardSortBy, setCardSortBy] = useState("timeSpent"); // "timeSpent" | "cardNumber" | "status"
  const [cardSortOrder, setCardSortOrder] = useState("desc");

  // Fetch entries data
  const fetchEntries = useCallback(async (page = 1, filtersOverride = filters, limitOverride = rowsPerPage) => {
    if (!projectId) return;

    try {
      setLoading(true);
      const response = await timeEntryAPI.getProjectEntries(projectId, {
        ...filtersOverride,
        page,
        limit: limitOverride,
      });

      if (response.data.success) {
        setEntries(response.data.entries);
        setStats(response.data.stats);
        setFilterOptions(response.data.filterOptions);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error("Error fetching time entries:", error);
      showToast("Failed to load time entries", "error");
    } finally {
      setLoading(false);
    }
  }, [projectId, filters, rowsPerPage, showToast]);

  // Fetch summary data
  const fetchSummary = useCallback(async () => {
    if (!projectId) return;

    try {
      const response = await timeEntryAPI.getProjectSummary(projectId);
      if (response.data.success) {
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchSummary();
      fetchEntries(1);
    }
  }, [isOpen, projectId, fetchSummary, fetchEntries]);

  // Apply filters
  const handleApplyFilters = () => {
    fetchEntries(1);
    setShowFilters(false);
    setShowMemberFilter(false);
    setShowLabelFilter(false);
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setSearchQuery("");
    setShowMemberFilter(false);
    setShowLabelFilter(false);
    fetchEntries(1, emptyFilters);
  };

  // Pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      fetchEntries(newPage);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const nextRowsPerPage = Number(event.target.value);
    setRowsPerPage(nextRowsPerPage);
    fetchEntries(1, filters, nextRowsPerPage);
  };

  // Edit entry
  const handleStartEdit = (entry) => {
    const hours = Math.floor(entry.duration / 3600);
    const minutes = Math.floor((entry.duration % 3600) / 60);
    setEditDuration({ hours: hours.toString(), minutes: minutes.toString() });
    setEditDescription(entry.description || "");
    setEditingEntry(entry._id);
  };

  const handleSaveEdit = async (entryId) => {
    const duration =
      (parseInt(editDuration.hours) || 0) * 3600 +
      (parseInt(editDuration.minutes) || 0) * 60;

    if (duration <= 0) {
      showToast("Duration must be greater than 0", "error");
      return;
    }

    try {
      await timeEntryAPI.updateEntry(entryId, {
        duration,
        description: editDescription,
      });
      showToast("Time entry updated", "success");
      setEditingEntry(null);
      fetchEntries(pagination.page);
      fetchSummary();
    } catch (error) {
      showToast(error.response?.data?.message || "Failed to update entry", "error");
    }
  };

  // Delete entry
  const handleDelete = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this time entry?")) {
      return;
    }

    try {
      await timeEntryAPI.deleteEntry(entryId);
      showToast("Time entry deleted", "success");
      fetchEntries(pagination.page);
      fetchSummary();
    } catch (error) {
      showToast(error.response?.data?.message || "Failed to delete entry", "error");
    }
  };

  // Export to CSV
  const handleExport = async () => {
    try {
      const allEntriesRes = await timeEntryAPI.getProjectEntries(projectId, {
        ...filters,
        limit: 10000,
      });

      if (!allEntriesRes.data.success) {
        throw new Error("Failed to fetch entries");
      }

      const allEntries = allEntriesRes.data.entries;

      const headers = ["Date", "User", "Card #", "Card Title", "Duration (hours)", "Type", "Description"];
      const rows = allEntries.map((entry) => [
        new Date(entry.workDate).toLocaleDateString(),
        entry.user?.name || "Unknown",
        entry.card?.cardNumber || "",
        `"${(entry.card?.title || "").replace(/"/g, '""')}"`,
        (entry.duration / 3600).toFixed(2),
        entry.entryType,
        `"${(entry.description || "").replace(/"/g, '""')}"`,
      ]);

      const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `time-report-${projectName || "project"}-${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      showToast("Report exported successfully", "success");
    } catch (error) {
      showToast("Failed to export report", "error");
    }
  };

  // Get sorted cards for cards view
  const getSortedCards = () => {
    if (!summary?.byCard) return [];

    let cards = [...summary.byCard];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      cards = cards.filter(
        (item) =>
          item.card?.title?.toLowerCase().includes(query) ||
          item.card?.cardNumber?.toString().includes(query)
      );
    }

    // Sort
    cards.sort((a, b) => {
      let aVal, bVal;
      switch (cardSortBy) {
        case "cardNumber":
          aVal = a.card?.cardNumber || 0;
          bVal = b.card?.cardNumber || 0;
          break;
        case "estimated":
          aVal = a.card?.estimatedTime || 0;
          bVal = b.card?.estimatedTime || 0;
          break;
        default:
          aVal = a.timeSpent || 0;
          bVal = b.timeSpent || 0;
      }
      return cardSortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });

    return cards;
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some((v) =>
    Array.isArray(v) ? v.length > 0 : v !== ""
  ) || searchQuery;

  const toggleLabelFilter = (labelName) => {
    setFilters((currentFilters) => {
      const labelNames = currentFilters.labelNames.includes(labelName)
        ? currentFilters.labelNames.filter((name) => name !== labelName)
        : [...currentFilters.labelNames, labelName];

      return {
        ...currentFilters,
        labelNames,
      };
    });
  };

  const toggleMemberFilter = (userId) => {
    setFilters((currentFilters) => {
      const userIds = currentFilters.userIds.includes(userId)
        ? currentFilters.userIds.filter((id) => id !== userId)
        : [...currentFilters.userIds, userId];

      return {
        ...currentFilters,
        userIds,
      };
    });
  };

  const selectedMemberText = filters.userIds.length === 0
    ? "All"
    : filters.userIds.length === 1
      ? filterOptions.users.find((u) => u._id === filters.userIds[0])?.name || "1 selected"
      : `${filters.userIds.length} selected`;

  const selectedLabelText = filters.labelNames.length === 0
    ? "All"
    : filters.labelNames.length === 1
      ? filters.labelNames[0]
      : `${filters.labelNames.length} selected`;

  const activeRowsPerPage = pagination.limit || rowsPerPage;
  const paginationStart = pagination.total > 0
    ? (pagination.page - 1) * activeRowsPerPage + 1
    : 0;
  const paginationEnd = pagination.total > 0
    ? Math.min(pagination.page * activeRowsPerPage, pagination.total)
    : 0;

  if (!isOpen) return null;

  const sortedCards = getSortedCards();

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 w-full h-full flex flex-col overflow-hidden transition-colors duration-200">
        {/* Header */}
        <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shadow-sm transition-colors duration-200">
          <div className="px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-slate-400" />
                </button>
                <div>
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Time Reports</h1>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">{projectName}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* View Mode Toggle */}
                {/* <div className="flex items-center bg-gray-100 dark:bg-slate-800 rounded-lg p-1 transition-colors">
                  <button
                    onClick={() => setViewMode("cards")}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${viewMode === "cards"
                      ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200"
                      }`}
                  >
                    Cards View
                  </button>
                  <button
                    onClick={() => setViewMode("entries")}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${viewMode === "entries"
                      ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200"
                      }`}
                  >
                    All Entries
                  </button>
                </div> */}

                {/* Export Button */}
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-slate-700 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-slate-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-medium">Export CSV</span>
                </button>

                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="px-6 py-3 h-full flex flex-col">
            <div className=" px-4 py-5 mb-4 border border-gray-200 rounded-lg bg-gray-50">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search cards..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-900 text-gray-900 dark:text-white w-64 transition-colors"
                    />
                  </div>
                </div>

                {/* Sort Controls for Cards View */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-slate-400">Sort by:</span>
                  <select
                    value={cardSortBy}
                    onChange={(e) => setCardSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors"
                  >
                    <option value="timeSpent">Time Spent</option>
                    <option value="cardNumber">Card Number</option>
                    <option value="estimated">Estimated Time</option>
                  </select>
                  <button
                    onClick={() => setCardSortOrder(cardSortOrder === "asc" ? "desc" : "asc")}
                    className="p-2 border border-gray-200 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    <ArrowUpDown className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                  </button>
                </div>
              </div>

              {/* Filters Panel for Entries View */}
              <div className="mb-2 p-2.5 bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 transition-colors">
                <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">End Date</label>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Member</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowMemberFilter(!showMemberFilter)}
                        className="w-full px-2 py-1.5 border border-gray-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-800 text-gray-900 dark:text-white flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{selectedMemberText}</span>
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                      </button>
                      {showMemberFilter && (
                        <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded shadow-lg max-h-56 overflow-y-auto">
                          {filterOptions.users.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400">
                              No members found
                            </div>
                          ) : (
                            filterOptions.users.map((member) => (
                              <label
                                key={member._id}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={filters.userIds.includes(member._id)}
                                  onChange={() => toggleMemberFilter(member._id)}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="truncate">{member.name}</span>
                              </label>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Card</label>
                    <select
                      value={filters.cardId}
                      onChange={(e) => setFilters({ ...filters, cardId: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    >
                      <option value="">All</option>
                      {filterOptions.cards.map((c) => (
                        <option key={c._id} value={c._id}>#{c.cardNumber} {c.title?.substring(0, 20)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Labels</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowLabelFilter(!showLabelFilter)}
                        className="w-full px-2 py-1.5 border border-gray-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-800 text-gray-900 dark:text-white flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{selectedLabelText}</span>
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                      </button>
                      {showLabelFilter && (
                        <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded shadow-lg max-h-56 overflow-y-auto">
                          {(filterOptions.labels || []).length === 0 ? (
                            <div className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400">
                              No labels found
                            </div>
                          ) : (
                            (filterOptions.labels || []).map((label) => (
                              <label
                                key={label.name}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={filters.labelNames.includes(label.name)}
                                  onChange={() => toggleLabelFilter(label.name)}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="truncate">{label.name}</span>
                              </label>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Type</label>
                    <select
                      value={filters.entryType}
                      onChange={(e) => setFilters({ ...filters, entryType: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    >
                      <option value="">All</option>
                      <option value="timer">Timer</option>
                      <option value="manual">Manual</option>
                    </select>
                  </div>
                  <button
                    onClick={handleApplyFilters}
                    className="px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                  >
                    Apply
                  </button>
                </div>
                <div className="mt-1 mr-5 flex justify-end">
                  {hasActiveFilters && (
                    <button
                      onClick={handleClearFilters}
                      className="text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              </div>
              {/* Summary Stats - Compact */}
              {summary && (
                <div className="bg-white dark:bg-slate-800 border border-gray-200 rounded-lg transition-colors">
                  <div className="px-6 py-2">
                    <div className="flex items-center justify-between gap-8">
                      {/* Stats Row */}
                      <div className="flex items-center gap-8">
                        {/* Total Logged Time */}
                        {viewMode === "entries" && stats && (
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                              <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-slate-400">Logged</p>
                              <p className="text-base font-bold text-gray-900 dark:text-white">{formatDuration(stats.totalDuration)}</p>
                            </div>
                          </div>
                        )}

                        {/* Estimated Time */}
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Estimated</p>
                            <p className="text-base font-bold text-gray-900 dark:text-white">{formatDuration(summary.totalEstimatedTime)}</p>
                          </div>
                        </div>

                        {/* Progress */}
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${summary.percentComplete > 100 ? "bg-red-100 dark:bg-red-900/30" : summary.percentComplete > 80 ? "bg-yellow-100 dark:bg-yellow-900/30" : "bg-green-100 dark:bg-green-900/30"
                            }`}>
                            {summary.percentComplete > 100 ? (
                              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                            ) : (
                              <CheckCircle className={`w-4 h-4 ${summary.percentComplete > 80 ? "text-yellow-600 dark:text-yellow-400" : "text-green-600 dark:text-green-400"}`} />
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Progress</p>
                            <p className={`text-base font-bold ${summary.percentComplete > 100 ? "text-red-600 dark:text-red-400" : summary.percentComplete > 80 ? "text-yellow-600 dark:text-yellow-400" : "text-green-600 dark:text-green-400"
                              }`}>{summary.percentComplete}%</p>
                          </div>
                        </div>

                        {/* Contributors */}
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                            <Users className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Team</p>
                            <div className="flex items-center gap-1.5">
                              <p className="text-base font-bold text-gray-900 dark:text-white">{summary.byUser?.length || 0}</p>
                              {summary.byUser?.length > 0 && (
                                <div className="flex -space-x-1.5">
                                  {summary.byUser.slice(0, 3).map((item) => (
                                    <Avatar key={item.user._id} user={item.user} size="xs" />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar - Inline */}
                      {summary.totalEstimatedTime > 0 && (
                        <div className="flex-1 max-w-xs">
                          <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5 transition-colors">
                            <div
                              className={`h-1.5 rounded-full transition-all ${summary.percentComplete > 100 ? "bg-red-500" : summary.percentComplete > 80 ? "bg-yellow-500" : "bg-green-500"
                                }`}
                              style={{ width: `${Math.min(summary.percentComplete, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Content Area */}
            <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-slate-900/50 rounded-lg border border-gray-200 dark:border-slate-700 transition-colors">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
                </div>
              ) : viewMode === "cards" ? (
                /* Cards View */
                <div>
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider sticky top-0 z-10">
                    <div className="col-span-1">#</div>
                    <div className="col-span-5">Card</div>
                    <div className="col-span-2 text-right">Spent</div>
                    <div className="col-span-2 text-right">Est.</div>
                    <div className="col-span-2 text-right">Progress</div>
                  </div>

                  {/* Cards List */}
                  {sortedCards.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-slate-500 bg-white dark:bg-slate-800 rounded-b-lg">
                      <FileText className="w-10 h-10 mb-3 text-gray-300 dark:text-slate-700" />
                      <p className="text-sm font-medium">No cards with time tracked</p>
                      <p className="text-xs">Start tracking time on cards to see them here</p>
                    </div>
                  ) : (
                    <div>
                      {sortedCards.map((item, index) => {
                        const progress = item.card?.estimatedTime > 0
                          ? Math.round((item.timeSpent / item.card.estimatedTime) * 100)
                          : 0;
                        const isOverTime = progress > 100;
                        const isNearLimit = progress >= 80 && progress < 100;

                        return (
                          <div
                            key={item.card?._id}
                            className={`grid grid-cols-12 gap-3 px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors items-center cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-800/50'}`}
                            onClick={() => handleCardClick(item.card)}
                          >
                            <div className="col-span-1">
                              <span className="text-xs font-mono text-gray-400 dark:text-slate-500">
                                {item.card?.cardNumber}
                              </span>
                            </div>
                            <div className="col-span-5">
                              <p className="text-sm text-gray-900 dark:text-white truncate">{item.card?.title}</p>
                              <p className="text-xs text-gray-400 dark:text-slate-500">{item.entryCount} entries</p>
                            </div>
                            <div className="col-span-2 text-right">
                              <span className={`text-sm font-medium ${isOverTime ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white"}`}>
                                {formatDuration(item.timeSpent)}
                              </span>
                            </div>
                            <div className="col-span-2 text-right">
                              <span className="text-sm text-gray-500 dark:text-slate-400">
                                {item.card?.estimatedTime > 0 ? formatDuration(item.card.estimatedTime) : "-"}
                              </span>
                            </div>
                            <div className="col-span-2">
                              {item.card?.estimatedTime > 0 ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <div
                                      className={`h-1.5 rounded-full ${isOverTime ? "bg-red-500" : isNearLimit ? "bg-yellow-500" : "bg-green-500"}`}
                                      style={{ width: `${Math.min(progress, 100)}%` }}
                                    />
                                  </div>
                                  <span className={`text-xs font-medium w-10 text-right ${isOverTime ? "text-red-600 dark:text-red-400" : isNearLimit ? "text-yellow-600 dark:text-yellow-400" : "text-green-600 dark:text-green-400"}`}>
                                    {progress}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400 dark:text-slate-500">-</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                /* Entries View */
                <div>
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider sticky top-0 z-10">
                    <div className="col-span-2">Date</div>
                    <div className="col-span-2">Member</div>
                    <div className="col-span-4">Card</div>
                    <div className="col-span-2 text-right">Duration</div>
                    <div className="col-span-1">Type</div>
                    <div className="col-span-1"></div>
                  </div>

                  {/* Entries List */}
                  {entries.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-slate-500 bg-white dark:bg-slate-800 rounded-b-lg">
                      <Clock className="w-10 h-10 mb-3 text-gray-300 dark:text-slate-700" />
                      <p className="text-sm font-medium">No time entries found</p>
                      <p className="text-xs">Adjust your filters or start tracking time</p>
                    </div>
                  ) : (
                    <div>
                      {entries.map((entry, index) => (
                        <div
                          key={entry._id}
                          className={`grid grid-cols-12 gap-3 px-4 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors items-center ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-800/50'}`}
                        >
                          {editingEntry === entry._id ? (
                            // Edit Mode
                            <div className="col-span-12 flex items-center gap-3 py-1">
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min="0"
                                  value={editDuration.hours}
                                  onChange={(e) => setEditDuration({ ...editDuration, hours: e.target.value })}
                                  className="w-14 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-center text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                  placeholder="h"
                                />
                                <span className="text-gray-400 dark:text-slate-500 text-sm">h</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="59"
                                  value={editDuration.minutes}
                                  onChange={(e) => setEditDuration({ ...editDuration, minutes: e.target.value })}
                                  className="w-14 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-center text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                  placeholder="m"
                                />
                                <span className="text-gray-400 dark:text-slate-500 text-sm">m</span>
                              </div>
                              <input
                                type="text"
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                placeholder="Description"
                                className="flex-1 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                              />
                              <button
                                onClick={() => handleSaveEdit(entry._id)}
                                className="px-3 py-1 bg-blue-600 dark:bg-blue-500 text-white text-sm rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingEntry(null)}
                                className="px-3 py-1 text-gray-500 dark:text-slate-400 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="col-span-2">
                                <span className="text-sm text-gray-600 dark:text-slate-400">{formatWorkDate(entry.workDate)}</span>
                              </div>
                              <div className="col-span-2">
                                <div className="flex items-center gap-1.5">
                                  <Avatar user={entry.user} size="xs" />
                                  <span className="text-sm text-gray-900 dark:text-white truncate">{entry.user?.name || "Unknown"}</span>
                                </div>
                              </div>
                              <div
                                className="col-span-4 cursor-pointer hover:text-blue-600 transition-colors"
                                onClick={() => handleCardClick(entry.card)}
                              >
                                <p className="text-sm text-gray-900 dark:text-white truncate">
                                  <span className="text-gray-400 dark:text-slate-500 font-mono text-xs mr-1">#{entry.card?.cardNumber}</span>
                                  {entry.card?.title}
                                </p>
                                {entry.description && (
                                  <p className="text-xs text-gray-400 dark:text-slate-500 truncate italic">"{entry.description}"</p>
                                )}
                              </div>
                              <div className="col-span-2 text-right">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{formatDuration(entry.duration)}</span>
                              </div>
                              <div className="col-span-1">
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded ${entry.entryType === "timer" ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                                  }`}>
                                  {entry.entryType === "timer" ? <Play className="w-2.5 h-2.5" /> : <PenLine className="w-2.5 h-2.5" />}
                                  {entry.entryType === "timer" ? "Timer" : "Manual"}
                                </span>
                              </div>
                              <div className="col-span-1 flex justify-end">
                                {entry.user?._id === user?._id && (
                                  <div className="flex items-center">
                                    <button
                                      onClick={() => handleStartEdit(entry)}
                                      className="p-1 text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 rounded transition-colors"
                                      title="Edit"
                                    >
                                      <PenLine className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(entry._id)}
                                      className="p-1 text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  {pagination.total > 0 && (
                    <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between transition-colors">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-gray-400 dark:text-slate-500">Rows per page</span>
                        <select
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                          className="h-8 px-2 pr-7 border border-blue-400 dark:border-blue-500 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={75}>75</option>
                          <option value={100}>100</option>
                        </select>
                        <span className="text-gray-300 dark:text-slate-600">|</span>
                        <span className="text-gray-600 dark:text-slate-300">
                          Showing {paginationStart}-{paginationEnd} of {pagination.total}
                        </span>
                      </div>
                      <div className="flex items-center gap-6">
                        <button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page === 1}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-sm text-gray-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          &lt;- Prev
                        </button>
                        <span className="text-sm font-semibold text-gray-700 dark:text-slate-200">
                          {pagination.page} / {pagination.pages}
                        </span>
                        <button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page === pagination.pages}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-sm text-gray-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          Next -&gt;
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              )}
            </div>

            {/* Bottom Stats Bar */}
            {viewMode === "cards" && summary && (
              <div className="mt-2 flex items-center justify-between text-sm text-gray-500 dark:text-slate-400 transition-colors">
                <span>
                  {sortedCards.length} cards with time tracked • {summary.cardsWithTime} total
                </span>
                <span>
                  Total: <span className="font-semibold text-gray-900 dark:text-white">{formatDuration(summary.totalTimeSpent)}</span>
                </span>
              </div>
            )}

            {viewMode === "entries" && stats && (
              <div className="mt-2 flex items-center justify-between text-sm text-gray-500 dark:text-slate-400 transition-colors">
                <span>
                  {stats.totalEntries} entries • {stats.timerEntries} timer • {stats.manualEntries} manual
                </span>
                <span>
                  Total: <span className="font-semibold text-gray-900 dark:text-white" style={{ marginRight: '18rem' }}>{formatDuration(stats.totalDuration)}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card Modal */}
      {showCardModal && selectedCard && (
        <CardModal
          card={selectedCard}
          onClose={handleCardModalClose}
          onCardUpdated={handleCardUpdated}
          onCardDeleted={handleCardDeleted}
        />
      )}

      {/* Loading Overlay for Card Fetching */}
      {loadingCard && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-xl flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Loading card...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeReportsModal;
